// @TODO: Icon support

import { forwardRef, useEffect, useState } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { Error } from "../Error";
import { Help } from "../Help";
import { Label, labelSizes, labelVariants } from "../Label";
import { Suffix, suffixSizes } from "../Suffix";
import styles from "./TextField.style";

const TextFieldStyled = styled.div((props) => ({ ...styles(props) }));

const TextField = forwardRef(
    (
        {
            labelAlignLeft,
            labelWidth,
            labelComponent,
            labelIconComponent,
            labelIsMobileOnly,
            type,
            name,
            id,
            value,
            label,
            placeholder,
            onBlur,
            onChange,
            onClick,
            onFocus,
            onKeyDown,
            onKeyUp,
            readOnly,
            required,
            disabled,
            autoComplete,
            error,
            errorMessage,
            helpText,
            variant,
            size,
            hasBottomSpacing,
            fieldWidth,
            inputWidth,
            actionComponent: Action,
            actionComponentRenderCondition,
            suffix,
            suffixWidth,
            className,
            isActionComponentTitle,
            clearErrors,
            ...rest
        },
        ref,
    ) => {
        const [focused, setFocused] = useState(false);

        // Blur won't fire when the disabled state is set on a focused input.
        useEffect(() => {
            if (disabled && focused) {
                setFocused(false);
                if (onBlur) {
                    onBlur();
                }
            }
        }, [disabled, focused, onBlur]);

        // onFocus
        const handleFocus = (event) => {
            if (disabled) {
                event.stopPropagation();
                return;
            }
            if (onFocus) {
                onFocus(event);
            }
            setFocused(true);
        };

        // onBlur
        const handleBlur = (event) => {
            if (onBlur) {
                onBlur(event);
            }
            setFocused(false);
        };

        // onChange
        const handleChange = (event, ...args) => {
            if (onChange) {
                onChange(event, ...args);
            }

            // Recipe uploader specific override
            if (name === "title" && event.target.value?.length) {
                clearErrors();
            }
        };

        return (
            <TextFieldStyled
                labelAlignLeft={labelAlignLeft}
                labelWidth={labelWidth}
                inputType={type}
                focused={focused}
                disabled={disabled}
                error={error}
                variant={variant}
                size={size}
                fieldWidth={fieldWidth}
                inputWidth={inputWidth}
                className={className}
                hasBottomSpacing={hasBottomSpacing}
                isActionComponentTitle={isActionComponentTitle}
                {...rest}
            >
                <Label
                    className="label"
                    label={label}
                    labelComponent={labelComponent}
                    iconComponent={labelIconComponent}
                    id={id}
                    htmlFor={id}
                    error={error}
                    required={required}
                    focused={focused}
                    variant={labelVariants.PRIMARY}
                    size={labelSizes[size?.toUpperCase()]}
                    width={labelWidth}
                    alignLeft={labelAlignLeft}
                    isMobileOnly={labelIsMobileOnly}
                />
                <div className="inputContainer">
                    <div className="inputContainer_inner">
                        <div className="inputWrap">
                            <input
                                ref={ref}
                                type={type}
                                name={name}
                                aria-invalid={!!error}
                                id={id}
                                value={value}
                                placeholder={placeholder}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                onClick={onClick}
                                onKeyDown={onKeyDown}
                                onKeyUp={onKeyUp}
                                readOnly={readOnly}
                                required={required}
                                disabled={disabled}
                                autoComplete={autoComplete}
                            />
                            {Action && !disabled && !!actionComponentRenderCondition && (
                                <div className="inputAction">
                                    <Action />
                                </div>
                            )}
                        </div>
                        <Suffix
                            label={suffix}
                            size={suffixSizes[size?.toUpperCase()]}
                            width={suffixWidth}
                            error={error}
                        />
                    </div>
                    <Error error={error} errorMessage={errorMessage} />
                    <Help text={helpText} />
                </div>
            </TextFieldStyled>
        );
    },
);

const textFieldVariants = Object.freeze({
    PRIMARY: "primary",
    SECONDARY: "secondary",
    TERTIARY: "tertiary",
    QUATERNARY: "quaternary",
});

const textFieldSizes = Object.freeze({
    NORMAL: "normal",
    LARGE: "large",
});

TextField.propTypes = {
    type: PropTypes.string,
    name: PropTypes.string,
    id: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    labelAlignLeft: PropTypes.bool,
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    labelComponent: PropTypes.node,
    labelIconComponent: PropTypes.node,
    labelIsMobileOnly: PropTypes.bool,
    placeholder: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyDown: PropTypes.func,
    onKeyUp: PropTypes.func,
    clearErrors: PropTypes.func,
    hasBottomSpacing: PropTypes.bool,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    autoComplete: PropTypes.string, // (https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill)
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    helpText: PropTypes.string,
    variant: PropTypes.oneOf([...Object.values(textFieldVariants)]),
    size: PropTypes.oneOf([...Object.values(textFieldSizes)]),
    fieldWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inputWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    actionComponent: PropTypes.elementType,
    actionComponentRenderCondition: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
    ]),
    suffix: PropTypes.string,
    suffixWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    isActionComponentTitle: PropTypes.bool,
};

TextField.defaultProps = {
    type: "text",
    name: undefined,
    id: undefined,
    value: undefined,
    label: undefined,
    labelAlignLeft: false,
    labelWidth: undefined,
    labelComponent: undefined,
    labelIconComponent: undefined,
    labelIsMobileOnly: false,
    placeholder: undefined,
    hasBottomSpacing: false,
    onBlur: () => {},
    onChange: () => {},
    onClick: () => {},
    onFocus: () => {},
    onKeyDown: () => {},
    onKeyUp: () => {},
    clearErrors: () => {},
    readOnly: false,
    required: false,
    disabled: false,
    autoComplete: undefined,
    error: false,
    errorMessage: undefined,
    helpText: undefined,
    variant: textFieldVariants.PRIMARY,
    size: textFieldSizes.NORMAL,
    fieldWidth: undefined,
    inputWidth: undefined,
    actionComponent: undefined,
    actionComponentRenderCondition: true,
    suffix: undefined,
    suffixWidth: undefined,
    className: undefined,
    isActionComponentTitle: false,
};

export { textFieldVariants, textFieldSizes };

export default TextField;
